import { useMemo } from "react";
import { useLocale } from "@acto/web-common/locales";
import type { TeamMeetingState } from "@acto/gql/graphql";
import type { MeetingCancellationSummary, MeetingSummary } from "@acto/taskpane/core/domain/TeamEmployee";

export interface AppointmentStateItem {
  id: string;
  date: string;
  clientId: string | null;
  clientName: string | null;
  clientExternalReference: string | null;
  clientAccessible: boolean;
  noteId: string | null;
  openTodosCount: number;
  description: string;
  cancellation: MeetingCancellationSummary | null;
  fromSignal: boolean;
}

export interface AppointmentStateData {
  employeeName: string;
  period: string;
  count: number;
  items: AppointmentStateItem[];
}

/**
 * A series counts once as planned from a signal: of its occurrences in the period, across every
 * state, only the earliest carries the signal. A one-off meeting carries its own.
 */
function meetingIdsCarryingSignal(appointments: MeetingSummary[]): Set<string> {
  const earliestBySeries = new Map<string, MeetingSummary>();
  const ids = new Set<string>();
  for (const meeting of appointments) {
    if (meeting.observationId == null) continue;
    if (meeting.seriesId == null) {
      ids.add(meeting.id);
      continue;
    }
    const earliest = earliestBySeries.get(meeting.seriesId);
    if (!earliest || meeting.scheduledDate.isBefore(earliest.scheduledDate)) {
      earliestBySeries.set(meeting.seriesId, meeting);
    }
  }
  for (const meeting of earliestBySeries.values()) ids.add(meeting.id);
  return ids;
}

/** Items are oldest first in every state; for no-report appointments that puts the longest-overdue report first. */
export function useAppointmentStateData(
  appointments: MeetingSummary[] | null,
  state: TeamMeetingState,
  employeeName: string,
  period: string,
): AppointmentStateData | null {
  const { locale } = useLocale();

  return useMemo(() => {
    if (!appointments) return null;

    const withSignal = meetingIdsCarryingSignal(appointments);
    const items = appointments
      .filter((m) => m.state === state)
      .sort((a, b) => a.scheduledDate.valueOf() - b.scheduledDate.valueOf())
      .map((m) => ({
        id: m.id,
        date: m.scheduledDate.locale(locale).format("D MMM"),
        clientId: m.clientId,
        clientName: m.clientName,
        clientExternalReference: m.clientExternalReference,
        clientAccessible: m.clientAccessible,
        noteId: m.noteId,
        openTodosCount: m.openTodosCount,
        description: m.description,
        cancellation: m.cancellation,
        fromSignal: withSignal.has(m.id),
      }));

    return { employeeName, period, count: items.length, items };
  }, [appointments, state, employeeName, period, locale]);
}
